import i18next from 'i18next';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats, ModuleType } from 'infrastructure/enums';
import { formatAuthorName } from 'utils/formatters';
import { DOCUMENTATION_TIME_PLACEHOLDER } from 'infrastructure/consts/tables';
import { convertSecondsToTime } from 'utils/timeHelper';
import { tableSortMap } from 'infrastructure/antd/functions/create-sort-order';
import { capitalize } from 'utils';

import NoteCommentsColumn from './noteComment';

import type {
  IClinicNotes,
  IClinicNotesAuthor,
} from 'infrastructure/interfaces';
import type { IBaseTableColumns } from 'components/atoms/base-table/types';
import type { TDeleteCommentFn } from 'infrastructure/hooks/rpm';
import type { SortState } from 'infrastructure/types';

export interface ColumnsProps {
  deleteNote: TDeleteCommentFn;
  documentationTimeSort: SortState;
  createdAtSort: SortState;
  module?: ModuleType;
}

export const columns = ({
  deleteNote,
  documentationTimeSort,
  createdAtSort,
  module,
}: ColumnsProps): IBaseTableColumns<IClinicNotes> => {
  const col: IBaseTableColumns<IClinicNotes> = [
    {
      label: i18next.t('labels.dateTime'),
      key: 'createdAt',
      width: '16%',
      render(value: string) {
        return (
          <span data-cy="clinic-notes-date">
            {DateFormatter({
              date: value,
              format: DateFormats['MM/dd/yyyy hh:mm:ss a'],
            })}
          </span>
        );
      },
      sortable: true,
      sortKey: 'createdAt',
      sortOrder: tableSortMap[createdAtSort],
    },
    {
      label: i18next.t('labels.documentTime'),
      key: 'documentationTime',
      width: '12%',
      render(documentationTime?: number | null) {
        const actionTime = !documentationTime
          ? DOCUMENTATION_TIME_PLACEHOLDER
          : convertSecondsToTime(documentationTime);
        return (
          <span data-cy="clinical-notes-documentation-time">{actionTime}</span>
        );
      },
      sortable: true,
      sortKey: 'documentationTime',
      sortOrder: tableSortMap[documentationTimeSort],
    },
    {
      label: i18next.t('labels.electronicallySignedBy'),
      key: 'author',
      width: '22%',
      render(author: IClinicNotesAuthor) {
        return (
          <span data-cy="clinic-notes-author">
            {formatAuthorName({
              title: author.title,
              firstName: author.firstName,
              lastName: author.lastName,
              professionType: author.profession,
            })}
          </span>
        );
      },
    },
    {
      label: i18next.t('labels.noteComment'),
      key: 'note',
      width: '50%',
      responsive: ['md'],
      render(value: string, record) {
        return (
          <NoteCommentsColumn
            value={value}
            record={record}
            deleteNote={deleteNote}
          />
        );
      },
    },
  ];

  if (module === ModuleType.APCM) {
    col.push({
      label: i18next.t('labels.type'),
      key: 'templateType',
      width: '8%',
      align: 'center',
      render(value: string) {
        return (
          <span data-cy="clinical-notes-template-type">
            {capitalize(value)}
          </span>
        );
      },
    });
  }

  return col;
};
